import "./App.scss";
import { Fragment, useEffect, useRef } from "react";
import IdleTimer from "react-idle-timer";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import MainWrapper from "./components/Shared/MainWrapper";
import AdminDashboard from "./components/Pages/AdminDashboard";
import ApplicationDetails from "./components/Pages/ApplicationDetails";
import Login from "./components/Pages/Login";
import PrivateRoute from "./components/Shared/PrivateRoute";
import CognitoRedirectUri from "./components/Shared/CognitoRedirectUri";
import AuthService, {
  SESSION_EXPIRED_NOTIFICATION,
} from "./services/AuthService";
import { getConfigs } from "./configs/Config";
import { getIdTokenExpiry, isAuthenticated } from "./utils/Auth";
import { useAppDispatch } from "./hooks/redux";
import { setNotification } from "./store/features/applications/applicationsSlice";
import "../node_modules/bootstrap/dist/css/bootstrap.min.css";

// Floor for the renewal timer so a token issued with less than minTokenTimeLeft can't spin it.
const MIN_RENEWAL_DELAY = 30 * 1000;

function App() {
  const { maxIdleTime, minTokenTimeLeft } = getConfigs();
  const dispatch = useAppDispatch();
  const renewalTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const environment = process.env.REACT_APP_ENVIRONMENT;
    const formatted =
      environment === "dev"
        ? " - Dev"
        : environment === "qa"
        ? " - QA"
        : environment === "stage"
        ? " - Stage"
        : "";

    document.title = `AvalonBay Admin Dashboard${formatted}`;
  }, []);

  const scheduleRenewal = () => {
    clearTimeout(renewalTimer.current);
    const expiry = getIdTokenExpiry();
    if (expiry === null) return;
    const delay = Math.max(
      expiry - minTokenTimeLeft - Date.now(),
      MIN_RENEWAL_DELAY
    );
    renewalTimer.current = setTimeout(keepSessionFresh, delay);
  };

  const keepSessionFresh = async () => {
    if (!isAuthenticated()) return;
    if (!(await AuthService.ensureFreshSession())) {
      clearTimeout(renewalTimer.current);
      dispatch(setNotification(SESSION_EXPIRED_NOTIFICATION));
      return;
    }
    scheduleRenewal();
  };

  useEffect(() => {
    keepSessionFresh();
    return () => clearTimeout(renewalTimer.current);
  }, []);

  const handleIdle = () => {
    console.log(`User was idle for ${maxIdleTime}ms. Logging out...`);
    sessionStorage.clear();
    document.location = "/AdminDashboard/Login";
  };

  // Also checked on activity, since background tabs throttle the renewal timer
  const onAction = () => {
    keepSessionFresh();
  };

  return (
    <div className="App">
      <IdleTimer
        timeout={maxIdleTime}
        throttle={30000}
        element={document}
        onIdle={handleIdle}
        startOnMount={true}
        onAction={onAction}
      />
      <link rel="preconnect" href="https://www.googletagmanager.com"></link>
      <MainWrapper>
        <Router>
          <Fragment>
            <Routes>
              <Route path="/AdminDashboard/" element={<PrivateRoute />}>
                <Route
                  path="/AdminDashboard/Application-Details/:id"
                  element={<ApplicationDetails />}
                />
                <Route
                  path="/AdminDashboard/Applications"
                  element={<AdminDashboard />}
                />
              </Route>
              <Route path="/AdminDashboard/Login" element={<Login />} />
              <Route
                path="/AdminDashboard/Login-Success"
                element={<CognitoRedirectUri />}
              />
              <Route
                path="*"
                element={<Navigate to="/AdminDashboard/Login" replace />}
              />
            </Routes>
          </Fragment>
        </Router>
      </MainWrapper>
    </div>
  );
}

export default App;
